import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { emailSchema, type EmailFormData } from '../../lib/validators';
import { forgotPasswordApi } from '../../services/api';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { extractApiError } from '../../lib/errorCodes';
import { toast } from 'sonner';

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = async (data: EmailFormData) => {
    setIsLoading(true);
    try {
      await forgotPasswordApi(data.email);
      setEmailSent(true);
      toast.success(t('general.passwordSent'));
    } catch (error) {
      toast.error(extractApiError(error));
    } finally {
      setIsLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="text-center">
        <div className="mb-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('general.passwordSent')}</h2>
        </div>
        <a href="/" className="text-primary hover:text-secondary transition-colors text-sm">
          {t('general.backToLogin')}
        </a>
      </div>
    );
  }

  return (
    <>
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {t('general.subscriptionTitle')}
        </h2>
        <p className="text-gray-600">{t('general.invalidToken')}</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <Input
          label={t('general.emailLabel')}
          type="email"
          placeholder="votre.email@exemple.com"
          autoComplete="email"
          error={errors.email?.message}
          {...register('email')}
        />
        <Button type="submit" isLoading={isLoading}>
          {t('general.resetButton')}
        </Button>
      </form>

      <div className="mt-4 text-center">
        <a href="/" className="text-sm text-primary hover:text-secondary transition-colors">
          {t('general.backToLogin')}
        </a>
      </div>
    </>
  );
}
